import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Calendar, MapPin, AlertTriangle, Target } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GUTScoreDisplay } from './GUTScoreDisplay';
import { PlanoAcaoComDetalhes } from '@/hooks/usePlanoAcaoResponsavel';
import { cn } from '@/lib/utils';

interface PlanoAcaoCardProps {
  plano: PlanoAcaoComDetalhes;
  onClick?: () => void;
  calcularProgressoPlano: (plano: PlanoAcaoComDetalhes) => number;
  variant?: 'default' | 'compact';
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'aberto': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'em_andamento': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'concluido': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'cancelado': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'aberto': return 'ABERTO';
    case 'em_andamento': return 'EM ANDAMENTO';
    case 'concluido': return 'CONCLUÍDO';
    case 'cancelado': return 'CANCELADO';
    default: return status.toUpperCase();
  }
};

export const PlanoAcaoCard = ({ 
  plano, 
  onClick, 
  calcularProgressoPlano,
  variant = 'default' 
}: PlanoAcaoCardProps) => {
  const gutData = plano.avaliacao_gut?.[0];
  const progresso = calcularProgressoPlano(plano);
  const acoesConcluidas = plano.acoes?.filter(a => a.status === 'concluida').length || 0;
  const totalAcoes = plano.acoes?.length || 0;
  
  const isAtrasado = plano.status !== 'concluido' && 
    plano.prazo_conclusao && 
    new Date(plano.prazo_conclusao) < new Date();

  if (variant === 'compact') {
    return (
      <div
        className={cn(
          "p-3 rounded-lg bg-card border shadow-sm hover:shadow-md transition-all cursor-pointer",
          isAtrasado && "border-destructive/50"
        )}
        onClick={onClick}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="font-medium text-sm text-foreground line-clamp-2 flex-1">
            {plano.titulo}
          </h4>
          <Badge className={cn("text-xs shrink-0", getStatusColor(plano.status))}>
            {getStatusLabel(plano.status)}
          </Badge>
        </div>
        
        {gutData && (
          <div className="mb-2">
            <GUTScoreDisplay
              gravidade={gutData.gravidade}
              urgencia={gutData.urgencia}
              tendencia={gutData.tendencia}
              score={gutData.score_gut || undefined}
              prioridade={gutData.prioridade || undefined}
              variant="compact"
            />
          </div>
        )}
        
        {totalAcoes > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{acoesConcluidas}/{totalAcoes} ações</span>
              <span>{progresso}%</span>
            </div>
            <Progress value={progresso} className="h-1.5" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "p-4 rounded-lg bg-card border shadow-sm hover:shadow-md hover:border-orange-200 transition-all cursor-pointer",
        isAtrasado && "border-destructive/50 hover:border-destructive"
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-semibold text-foreground flex-1">{plano.titulo}</h4>
        <Badge className={getStatusColor(plano.status)}>
          {getStatusLabel(plano.status)}
        </Badge>
      </div>
      
      {/* GUT Score e Prioridade */}
      <div className="flex items-center gap-2 mb-3">
        {gutData && (
          <GUTScoreDisplay
            gravidade={gutData.gravidade}
            urgencia={gutData.urgencia}
            tendencia={gutData.tendencia}
            score={gutData.score_gut || undefined}
            prioridade={gutData.prioridade || undefined}
            variant="compact"
          />
        )}
        
        {isAtrasado && (
          <Badge variant="destructive" className="gap-1 text-xs">
            <AlertTriangle className="h-3 w-3" />
            Atrasado
          </Badge>
        )}
      </div>
      
      {/* Progress Bar de Ações */}
      {totalAcoes > 0 && (
        <div className="space-y-1 mb-3">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              <Target className="h-3 w-3" />
              Progresso das Ações
            </span>
            <span className="font-medium">{progresso}%</span>
          </div>
          <Progress value={progresso} className="h-2" />
          <span className="text-xs text-muted-foreground">
            {acoesConcluidas}/{totalAcoes} ações concluídas
          </span>
        </div>
      )}
      
      {/* Informações adicionais */}
      <div className="space-y-1 text-sm text-muted-foreground">
        {plano.prazo_conclusao && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>
              Prazo: {formatDistanceToNow(new Date(plano.prazo_conclusao), {
                addSuffix: true,
                locale: ptBR
              })}
            </span>
          </div>
        )}
        
        {plano.local && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>{plano.local}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanoAcaoCard;
